import React, { useEffect } from "react";
import { Row, Col, Form, Button, Card, Dropdown } from "react-bootstrap";
import LocationAutocomplete from "../LocationAutocomplete";
import { useLocation } from "react-router-dom";
import { FaPlus, FaTrash } from "react-icons/fa";
import { useTranslation } from "react-i18next";


const CustomTimeSelect = ({ value, onChange, options, error, placeholder }) => {
  return (
    <Dropdown className="flex-grow-1" style={{ minWidth: "75px" }}>
      <Dropdown.Toggle
        as="button"
        className={`form-select input-elegant px-2 py-2 d-flex justify-content-between align-items-center w-100 ${error ? "is-invalid" : ""}`}
        style={{
          backgroundColor: "#ffffff",
          border: "1px solid var(--dark-border, #e2e8f0)",
          color: "var(--text-dark, #0f172a)",
          borderRadius: "8px",
          cursor: "pointer",
          fontSize: "15px",
        }}
      >
        {value || placeholder}
      </Dropdown.Toggle>

      <Dropdown.Menu
        style={{
          maxHeight: "300px",
          overflowY: "auto",
          backgroundColor: "#ffffff",
          border: "1px solid var(--dark-border, #e2e8f0)",
          borderRadius: "8px",
          minWidth: "100%",
          padding: 0,
          boxShadow: "var(--shadow-md)",
        }}
      >
        {options.map((opt) => (
          <Dropdown.Item
            key={opt}
            onClick={() => onChange(opt)}
            style={{
              padding: "8px 16px",
              color: value === opt ? "var(--orange-primary, #ff6b00)" : "var(--text-dark, #0f172a)",
              backgroundColor: value === opt ? "var(--orange-light, #ffefe5)" : "transparent",
            }}
            className="custom-dropdown-item"
          >
            {opt}
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

const TimeSelect = ({ value, onChange, error, minTime = 7, maxTime = 18 }) => {
  const h24 = value ? value.split(":")[0].padStart(2, "0") : "";
  const m = value ? value.split(":")[1].padStart(2, "0") : "";

  const handleHourChange = (newHour) => {
    onChange(`${newHour}:${m || "00"}`);
  };

  const handleMinuteChange = (newMinute) => {
    const finalHour = h24 === "" ? "08" : h24;
    onChange(`${finalHour}:${newMinute}`);
  };

  const hourOptions = [...Array(24).keys()]
    .filter((hr) => hr >= minTime && hr <= maxTime)
    .map((hr) => hr.toString().padStart(2, "0"));
  const minuteOptions = ["00", "15", "30", "45"];

  return (
    <div className="d-flex gap-1 align-items-center">
      <CustomTimeSelect
        value={h24}
        onChange={handleHourChange}
        options={hourOptions}
        error={error}
        placeholder="--"
      />
      <span className="fw-bold text-dark">:</span>
      <CustomTimeSelect
        value={m}
        onChange={handleMinuteChange}
        options={minuteOptions}
        error={error}
        placeholder="--"
      />
    </div>
  );
};

const emptyAddress = () => {
  const now = new Date();
  const roundedTotalMinutes = Math.ceil(
    (now.getHours() * 60 + now.getMinutes()) / 15,
  ) * 15;
  // A same-day booking must also leave two hours before the 18:00 cutoff.
  const bookingToday = roundedTotalMinutes <= 16 * 60;
  const date = new Date(now);

  if (!bookingToday) {
    date.setDate(date.getDate() + 1);
  }

  const firstTotalMinutes = bookingToday
    ? Math.max(7 * 60, roundedTotalMinutes)
    : 7 * 60;
  const firstHour = Math.floor(firstTotalMinutes / 60);
  const firstMinutes = firstTotalMinutes % 60;
  const timeFrom = `${String(firstHour).padStart(2, "0")}:${String(
    firstMinutes,
  ).padStart(2, "0")}`;
  const timeTo = `${String(firstHour + 2).padStart(2, "0")}:00`;

  return {
    companyName: "",
    firstName: "",
    lastName: "",
    street: "",
    addressExtra: "",
    postalCity: "",
    postalCity_lat: "",
    postalCity_lng: "",
    phone: "",
    pickupReference: "",
    pickupDate: date.toISOString().split("T")[0],
    timeFrom,
    timeTo,
  };
};

function AddressForm({ data, onChange, index, validationErrors = {} }) {
  const location = useLocation();
  const { state } = location;
  const { t } = useTranslation();

  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;
  const minimumDate = now.getHours() >= 18 ? (() => {
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(
      tomorrow.getDate(),
    ).padStart(2, "0")}`;
  })() : today;
  const isToday = data.pickupDate === today;
  const currentHour = now.getHours();
  const minimumStartHour = isToday ? Math.max(7, currentHour) : 7;
  const selectedStartHour = Number(data.timeFrom?.split(":")[0]);
  // Same-day bookings require two hours; future dates require one hour.
  const minimumEndHour = Math.max(
    isToday ? 9 : 8,
    selectedStartHour + 0 || (isToday ? 9 : 8),
  );

  const previousPickupAddress =
    state?.formData?.abholung?.[index]?.address || "";

  const handleLocationChange = (e) => {
    let value = e?.target?.value || e?.value || "";
    const lat = e?.lat || "";
    const lng = e?.lng || "";

    if (typeof value === "object") {
      value =
        value.address ||
        value.label ||
        value.text ||
        value.value ||
        JSON.stringify(value) ||
        "";
    }

    if (!value && previousPickupAddress) {
      value = previousPickupAddress;
    }

    onChange("postalCity", value);
    onChange("postalCity_lat", lat);
    onChange("postalCity_lng", lng);
  };

  useEffect(() => {
    if (previousPickupAddress && !data.postalCity) {
      onChange("postalCity", previousPickupAddress);
    }
  }, [previousPickupAddress, data.postalCity, onChange]);

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label className="fw-bold with-icon">
          {t("company_name")}
        </Form.Label>
        <Form.Control
          type="text"
          value={data.companyName}
          onChange={(e) => onChange("companyName", e.target.value)}
          className="input-elegant"
          data-error={`pickup_companyName_${index}`}
          isInvalid={!!validationErrors[`pickup_companyName_${index}`]}
        />
        <Form.Control.Feedback type="invalid">
          {validationErrors[`pickup_companyName_${index}`]}
        </Form.Control.Feedback>
      </Form.Group>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("first_name")} *
          </Form.Label>
          <Form.Control
            type="text"
            value={data.firstName}
            onChange={(e) => onChange("firstName", e.target.value)}
            className="input-elegant"
            data-error={`pickup_firstName_${index}`}
            isInvalid={!!validationErrors[`pickup_firstName_${index}`]}
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors[`pickup_firstName_${index}`]}
          </Form.Control.Feedback>
        </Col>
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("last_name")} *
          </Form.Label>
          <Form.Control
            type="text"
            value={data.lastName}
            onChange={(e) => onChange("lastName", e.target.value)}
            className="input-elegant"
            data-error={`pickup_lastName_${index}`}
            isInvalid={!!validationErrors[`pickup_lastName_${index}`]}
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors[`pickup_lastName_${index}`]}
          </Form.Control.Feedback>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("street_house")} *
          </Form.Label>
          <Form.Control
            type="text"
            value={data.street}
            onChange={(e) => onChange("street", e.target.value)}
            className="input-elegant"
            data-error={`pickup_street_${index}`}
            isInvalid={!!validationErrors[`pickup_street_${index}`]}
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors[`pickup_street_${index}`]}
          </Form.Control.Feedback>
        </Col>
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("address_extra_label")}
          </Form.Label>
          <Form.Control
            type="text"
            value={data.addressExtra}
            onChange={(e) => onChange("addressExtra", e.target.value)}
            className="input-elegant"
          />
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label className="fw-bold with-icon">
          {t("postal_city")} *
        </Form.Label>
        <LocationAutocomplete
          disabled
          type="text"
          name="postalCity"
          value={data.postalCity || previousPickupAddress}
          onChange={handleLocationChange}
          className="input-elegant"
          data-error={`pickup_postalCity_${index}`}
          isInvalid={!!validationErrors[`pickup_postalCity_${index}`]}
        />
        <Form.Control.Feedback type="invalid">
          {validationErrors[`pickup_postalCity_${index}`]}
        </Form.Control.Feedback>
        {previousPickupAddress && !data.postalCity && (
          <Form.Text className="text-muted">
            {t("previous_selection_used", { address: previousPickupAddress })}
          </Form.Text>
        )}
      </Form.Group>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("phone_number")}
          </Form.Label>
          <Form.Control
            type="number"
            value={data.phone}
            onChange={(e) => onChange("phone", e.target.value)}
            className="input-elegant"
          />
        </Col>
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">
            {t("reference_number")}
          </Form.Label>
          <Form.Control
            type="text"
            value={data.pickupReference}
            onChange={(e) => onChange("pickupReference", e.target.value)}
            className="input-elegant"
          />
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={6}>
          <Form.Label className="fw-bold with-icon">{t("date")} *</Form.Label>
          <Form.Control
            type="date"
            value={data.pickupDate}
            onChange={(e) => {
              const nextDate = e.target.value;
              onChange("pickupDate", nextDate);
              if (nextDate === today) {
                const startHour = Math.max(7, new Date().getHours());
                onChange("timeFrom", `${String(startHour).padStart(2, "0")}:00`);
                onChange("timeTo", `${String(Math.min(startHour + 2, 18)).padStart(2, "0")}:00`);
              } else {
                onChange("timeFrom", "07:00");
                onChange("timeTo", "09:00");
              }
            }}
            className="input-elegant"
            data-error={`pickup_date_${index}`}
            isInvalid={!!validationErrors[`pickup_date_${index}`]}
            min={minimumDate}
          />
          <Form.Control.Feedback type="invalid">
            {validationErrors[`pickup_date_${index}`]}
          </Form.Control.Feedback>
        </Col>
        <Col md={3}>
          <Form.Label className="fw-bold with-icon">{t("from")} *</Form.Label>
          <TimeSelect
            value={data.timeFrom}
            onChange={(val) => onChange("timeFrom", val)}
            error={validationErrors[`pickup_timeFrom_${index}`]}
            errorName={`pickup_timeFrom_${index}`}
            minTime={minimumStartHour}
            maxTime={18}
          />
          {validationErrors[`pickup_timeFrom_${index}`] && (
            <div className="invalid-feedback d-block">
              {validationErrors[`pickup_timeFrom_${index}`]}
            </div>
          )}
        </Col>
        <Col md={3}>
          <Form.Label className="fw-bold with-icon">{t("to")} *</Form.Label>
          <TimeSelect
            value={data.timeTo}
            onChange={(val) => onChange("timeTo", val)}
            error={validationErrors[`pickup_timeTo_${index}`]}
            errorName={`pickup_timeTo_${index}`}
            minTime={minimumEndHour}
            maxTime={18}
          />
          {validationErrors[`pickup_timeTo_${index}`] && (
            <div className="invalid-feedback d-block">
              {validationErrors[`pickup_timeTo_${index}`]}
            </div>
          )}
        </Col>
      </Row>
    </>
  );
}

export default function StepTwoWrapper({
  forms = [],
  setForms,
  validationErrors = {},
}) {
  const location = useLocation();
  const { state } = location;
  const { bookingData, orderFormData } = state || {};
  const { t } = useTranslation();

  useEffect(() => {
    if (!setForms) return;

    if (
      forms.length > 0 &&
      forms.some((f) => f.firstName || f.lastName || f.street)
    ) {
      return;
    }

    const previousPickupAddresses = state?.formData?.abholung || [];

    if (previousPickupAddresses.length > 0) {
      const initialForms = previousPickupAddresses.map((addressObj, index) => {
        const address =
          typeof addressObj === "string" ? addressObj : addressObj?.address;
        return {
          ...emptyAddress(),
          postalCity: address || "",
          postalCity_lat: addressObj?.lat || "",
          postalCity_lng: addressObj?.lng || "",
        };
      });
      setForms(initialForms);
    } else {
      setForms([emptyAddress()]);
    }
  }, [setForms, state]);

  const handleChange = (index, field, value) => {
    // Ensure postalCity is always a string
    const processedValue = field === "postalCity" ? String(value || "") : value;
    setForms((prev) =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: processedValue } : item,
      ),
    );
  };

  const addForm = () => setForms((prev) => [...prev, emptyAddress()]);

  const removeForm = (index) =>
    setForms((prev) => prev.filter((_, i) => i !== index));

  return (
    <>
      {forms.map((formData, idx) => (
        <Card key={idx} className="mb-4 p-3 border-0" style={{ borderRadius: "16px", backgroundColor: "#ffffff", border: "1px solid #e2e8f0" }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="m-0 text-dark fw-bold">
              {t("pickup_address_label")} {idx + 1}
            </h5>
            {forms.length > 1 && (
              <Button
                variant="outline-danger"
                size="sm"
                className="p-1 px-2"
                style={{ backgroundColor: "#fef2f2", borderColor: "#fee2e2", color: "#ef4444", borderRadius: "8px" }}
                onClick={() => removeForm(idx)}
              >
                <FaTrash />
              </Button>
            )}
          </div>

          <AddressForm
            data={formData}
            onChange={(field, value) => handleChange(idx, field, value)}
            index={idx}
            validationErrors={validationErrors}
          />
        </Card>
      ))}

      <div className="d-flex justify-content-end align-items-center mb-3">
        <Button
          variant="outline-warning"
          size="sm"
          className="fw-bold px-3 py-1 d-flex align-items-center"
          style={{ borderColor: "#ff6b00", color: "#ff6b00", borderRadius: "8px", backgroundColor: "#ffffff" }}
          onClick={addForm}
        >
          <FaPlus className="me-1" /> {t("add_address")}
        </Button>
      </div>
    </>
  );
}
